'use strict';

const {
  get,
  has,
  isBoolean,
  isFunction,
  isEqual,
  isFinite,
  isNil,
  isString,
  sumBy,
} = require('lodash');
const pgMustFlagOptions = require('../enums/pg-must-flag-option');
const sizeFieldsByFillInSelection = require('../enums/size-attributes');
const severity = require('../enums/validation-severity');

const defaultFields = Object.freeze({
  clientProductKey: false,
  minimumFacings: false,
  minimumDistribution: false,
  maximumDistribution: false,
  pgMustFlag: false,
  periodID: false,
  seasonalWeekNumbers: false,
  userEnteredSisterProduct: false,
  seasonalProduct: false,
  localProduct: false,
  averageTransactionWeight: true,
  productKeyDisplay: true,
  duplicateProductKey: true,
  duplicateFutureId: false,
  parentReferenceExists: false,
  dependentSourceReferenceExists: false,
  palletContentReferenceExists: true,
  unitWidth: false,
  unitLength: false,
  unitHeight: false,
  packageTypeDescription: false,
});

const createProductValidationOptions = ({
  clientConfig = {},
  scenario = {},
  workpackage = {},
  customAttributeIds = [],
} = {}) => {
  const features = get(clientConfig, 'features', {});
  const editorFeatures = get(features, 'productAttributeEditorPage', {});
  const additionalFieldsEnabled = editorFeatures.additionalFieldsEnabled === true;
  const additionalFields = get(scenario, 'optionalAttributes.additionalFields', {});
  const sizeFields = sizeFieldsByFillInSelection[workpackage.fillInSelection] ?? [];
  const duplicateKeyValidation = editorFeatures.duplicateKeyValidation === true;

  return {
    fields: {
      clientProductKey:
        get(features, 'scenario.products.displayOriginalClientKey', false) === true,
      minimumFacings:
        features.minimumFacingsEnabled === true &&
        get(scenario, 'optionalAttributes.useMinimumFacings', false) === true,
      minimumDistribution:
        (features.minimumDistributionEnabled === true &&
          get(scenario, 'optionalAttributes.useMinimumDistribution', false) === true) ||
        (additionalFieldsEnabled && additionalFields.minimumDistribution === true),
      maximumDistribution:
        additionalFieldsEnabled && additionalFields.maximumDistribution === true,
      pgMustFlag: additionalFieldsEnabled && additionalFields.pgMustFlag === true,
      periodID: editorFeatures.periodIDColumnEnabled === true,
      seasonalWeekNumbers:
        additionalFieldsEnabled && additionalFields.seasonalWeekNumbers === true,
      userEnteredSisterProduct:
        additionalFieldsEnabled && additionalFields.userEnteredSisterProduct === true,
      seasonalProduct: additionalFieldsEnabled && additionalFields.seasonalProduct === true,
      localProduct: additionalFieldsEnabled && additionalFields.localProduct === true,
      unitWidth: sizeFields.includes('unitWidth'),
      unitLength: sizeFields.includes('unitLength'),
      unitHeight: sizeFields.includes('unitHeight'),
      packageTypeDescription: features.isPackageTypeDescriptionColumnVisible === true,
      averageTransactionWeight: features.transactionWeightEnabled === true,
      duplicateProductKey: duplicateKeyValidation,
      duplicateFutureId: duplicateKeyValidation,
      parentReferenceExists: true,
      dependentSourceReferenceExists:
        get(features, 'scenario.products.dependentProductsEnabled', false) === true,
      palletContentReferenceExists: features.palletContentEnabled === true,
    },
    customAttributeIds,
    availablePeriodIds: workpackage.periodIds ?? [],
  };
};

const isBlank = value => isNil(value) || value === '';

const readValue = (product, field) => {
  if (has(product, [field])) return get(product, [field]);

  return product.customAttributes?.find(attribute => attribute.id === field)?.value;
};

const addIssue = (issues, field, { message, level = severity.error, params }) => {
  if (!issues[field]) issues[field] = {};
  if (!issues[field][level]) issues[field][level] = [];

  const issuesAtLevel = issues[field][level];
  const issue = params ? { message, params } : { message };

  if (!issuesAtLevel.some(existing => isEqual(existing, issue))) issuesAtLevel.push(issue);
};

const createRule = (isValid, message, options = {}) => (value, validationContext) =>
  isValid(value, validationContext) ? null : { message, ...options };

const required = createRule(value => !isBlank(value), 'required');
const number = createRule(isFinite, 'number');
const string = createRule(isString, 'string');
const boolean = createRule(isBoolean, 'boolean');
const greaterThan = min => createRule(value => value > min, 'gt', { params: { min } });
const greaterThanOrEqual = min => createRule(value => value >= min, 'gte', { params: { min } });
const lessThanOrEqual = max =>
  createRule(value => value <= max, 'lte', { params: { max } });
const oneOf = (allowedValues, message = 'invalidOption') =>
  createRule(value => allowedValues.includes(value), message);
const optional = validationRule => value =>
  isBlank(value) ? null : validationRule(value);

const positiveNumber = [number, greaterThan(0)];
const nonNegativeNumber = [required, number, greaterThanOrEqual(0)];
const optionalPositiveNumber = positiveNumber.map(optional);
const requiredPositiveNumber = [required, ...positiveNumber];
const requiredClientProductKey = createRule(value => !isBlank(value), 'requiredField');
const requiredPeriodId = createRule(value => !isBlank(value), 'requiredPeriodID');
const positiveMarginWarning = createRule(value => value > 0, 'gt', {
  params: { min: 0 },
  level: severity.warn,
});

const transactionWeightRequired = createRule(value => value > 0, 'kgProdNeedTransaction');

const selfReference = message =>
  createRule(
    (value, { product }) => isBlank(value) || value !== product.productKeyDisplay,
    message
  );

const parentAndDependentAreExclusive = createRule(
  (_, { product }) => isBlank(product.parentProduct) || isBlank(product.dependentSource),
  'parentAndDependent'
);

const uniqueProductKey = createRule(
  (value, { productContext }) =>
    isBlank(value) || productContext.productsByDisplayKey.get(value)?.length <= 1,
  'uniqueField',
  { level: severity.block }
);

const uniqueFutureId = createRule(
  (value, { productContext }) =>
    isBlank(value) || productContext.futureIdCounts.get(value) <= 1,
  'uniqueField',
  { level: severity.block }
);

const referenceExists = createRule(
  (value, { productContext }) =>
    isBlank(value) || productContext.productsByDisplayKey.has(value),
  'selectedProductKeyIsNotFromTheCurrentScenario'
);

const parentIsNotNew = createRule(
  (value, { productContext }) =>
    isBlank(value) ||
    !(productContext.productsByDisplayKey.get(value) ?? []).some(product => product.isNewProduct),
  'newProductCanNotBeParentProduct'
);

const relationshipDepth = (field, message) =>
  createRule(
    (value, { productContext }) =>
      isBlank(value) ||
      !(productContext.productsByDisplayKey.get(value) ?? []).some(
        product => !isBlank(product[field])
      ),
    message
  );

const palletIsNotSelfReference = selfReference(
  'palletedProductKeyCannotBeTheSameAsProductKey'
);

const parentIsNotSelfReference = selfReference('parentKeyCannotBeTheSameAsProductKey');

const dependentSourceIsNotSelfReference = selfReference(
  'dependentSourceCannotBeTheSameAsProductKey'
);

const parentRelationshipDepth = relationshipDepth(
  'parentProduct',
  'parentProductRelationTooDeep'
);

const dependentSourceRelationshipDepth = relationshipDepth(
  'dependentSource',
  'dependentSourceRelationTooDeep'
);

const getMarginRules = ({ product }) => {
  const productIsPallet = !isBlank(product.palletContent);
  return !productIsPallet && product.assortment
    ? [required, number, positiveMarginWarning]
    : [];
};

const getPriceRules = ({ product }) =>
  isBlank(product.palletContent) ? [required, number, greaterThan(0)] : [];

const getClientProductKeyRules = ({ product }) =>
  product.assortment ? [requiredClientProductKey] : [];

const getPeriodIdRules = ({ product, availablePeriodIds }) =>
  product.assortment && availablePeriodIds.length && !availablePeriodIds.includes(null)
    ? [requiredPeriodId, oneOf(availablePeriodIds, 'invalidPeriodID')]
    : [];

const getProductKeyRules = ({ fields }) => (fields.productKeyDisplay ? [required] : []);
const getUniqueProductKeyRules = ({ fields }) =>
  fields.duplicateProductKey ? [uniqueProductKey] : [];
const getPalletCollectionRules = ({ fields }) =>
  fields.palletContentReferenceExists ? [referenceExists] : [];
const getParentCollectionRules = ({ fields }) => [
  ...(fields.parentReferenceExists ? [referenceExists] : []),
  parentIsNotNew,
  parentRelationshipDepth,
];
const getDependentSourceCollectionRules = ({ fields }) => [
  ...(fields.dependentSourceReferenceExists ? [referenceExists] : []),
  dependentSourceRelationshipDepth,
];
const getTransactionWeightRules = ({ product }) => [
  ...optionalPositiveNumber,
  ...(product.packageTypeDescription?.toLowerCase() === 'kg'
    ? [transactionWeightRequired]
    : []),
];

const coreFieldDefinitions = [
  {
    field: 'productKeyDisplay',
    rules: getProductKeyRules,
    collectionRules: getUniqueProductKeyRules,
  },
  { field: 'itemDescription', rules: [required, string] },
  { field: 'contentValue', rules: requiredPositiveNumber },
  { field: 'contentUnitOfMeasure', rules: [required, string] },
  { field: 'assortment', rules: [required, boolean] },
  { field: 'theoreticalMargin', rules: getMarginRules },
  { field: 'theoreticalPrice', rules: getPriceRules },
  {
    field: 'palletContent',
    rules: [palletIsNotSelfReference],
    collectionRules: getPalletCollectionRules,
  },
  {
    field: 'parentProduct',
    rules: [parentIsNotSelfReference, parentAndDependentAreExclusive],
    collectionRules: getParentCollectionRules,
  },
  {
    field: 'dependentSource',
    rules: [dependentSourceIsNotSelfReference],
    collectionRules: getDependentSourceCollectionRules,
  },
];

const enabledFieldDefinitions = [
  {
    field: 'futureId',
    enabledBy: 'duplicateFutureId',
    collectionRules: [uniqueFutureId],
  },
  { field: 'clientProductKey', rules: getClientProductKeyRules },
  { field: 'periodID', rules: getPeriodIdRules },
  { field: 'packageTypeDescription', rules: [required] },
  { field: 'averageTransactionWeight', rules: getTransactionWeightRules },
  { field: 'unitWidth', rules: requiredPositiveNumber },
  { field: 'unitLength', rules: requiredPositiveNumber },
  { field: 'unitHeight', rules: requiredPositiveNumber },
  { field: 'minimumFacings', rules: nonNegativeNumber },
  { field: 'minimumDistribution', rules: nonNegativeNumber },
  { field: 'maximumDistribution', rules: nonNegativeNumber },
  { field: 'seasonalWeekNumbers', rules: nonNegativeNumber },
  { field: 'userEnteredSisterProduct', rules: [optional(string)] },
  { field: 'seasonalProduct', rules: [optional(boolean)] },
  {
    field: 'pgMustFlag',
    rules: [
      optional(string),
      optional(oneOf(Object.values(pgMustFlagOptions), 'invalidPgMustFlag')),
    ],
  },
  { field: 'localProduct', rules: [optional(boolean)] },
];

const assessField = (validationContext, field) => {
  validationContext.assessedFields.add(field);
};

const applyFieldRules = (validationContext, field, rules) => {
  const { issues, product } = validationContext;
  assessField(validationContext, field);
  const value = readValue(product, field);
  rules.forEach(validationRule => {
    const issue = validationRule(value, validationContext);
    if (issue) addIssue(issues, field, issue);
  });
};

const applyFieldDefinition = (validationContext, definition, ruleType = 'rules') => {
  const { field } = definition;
  const rules = definition[ruleType] ?? [];
  const applicableRules = isFunction(rules) ? rules(validationContext) : rules;
  if (applicableRules.length) applyFieldRules(validationContext, field, applicableRules);
};

const buildProductContext = (products, fields) => {
  const productsByDisplayKey = new Map();
  const futureIdCounts = new Map();

  products.forEach(product => {
    if (!isBlank(product.productKeyDisplay)) {
      const matchingProducts = productsByDisplayKey.get(product.productKeyDisplay);
      if (matchingProducts) matchingProducts.push(product);
      else productsByDisplayKey.set(product.productKeyDisplay, [product]);
    }

    if (fields.duplicateFutureId && !isBlank(product.futureId)) {
      futureIdCounts.set(product.futureId, (futureIdCounts.get(product.futureId) ?? 0) + 1);
    }
  });

  return { productsByDisplayKey, futureIdCounts };
};

const createValidationConfig = (options = {}) => {
  const fields = { ...defaultFields, ...options.fields };
  const fieldDefinitions = [
    ...coreFieldDefinitions,
    ...enabledFieldDefinitions.filter(({ field, enabledBy = field }) => fields[enabledBy]),
    ...(options.customAttributeIds ?? []).map(field => ({ field, rules: [required] })),
  ];

  return {
    fields,
    fieldDefinitions,
    availablePeriodIds: options.availablePeriodIds ?? [],
  };
};

const validateProductResultWithConfig = (product, validationConfig) => {
  const { fieldDefinitions } = validationConfig;
  const issues = {};
  const validationContext = {
    assessedFields: new Set(),
    issues,
    product,
    ...validationConfig,
  };

  fieldDefinitions.forEach(fieldDefinition => {
    applyFieldDefinition(validationContext, fieldDefinition);
  });

  return validationContext;
};

const validateProductWithConfig = (product, validationConfig) => {
  const { issues } = validateProductResultWithConfig(product, validationConfig);
  return Object.keys(issues).length ? issues : null;
};

const createProductValidator = (options = {}) => {
  const validationConfig = createValidationConfig(options);

  const validateProductsWithConfig = products => {
    const productContext = buildProductContext(products, validationConfig.fields);

    return products.map(product => {
      const validationResult = validateProductResultWithConfig(product, validationConfig);
      const validationContext = {
        ...validationResult,
        ...validationConfig,
        productContext,
      };

      validationConfig.fieldDefinitions.forEach(fieldDefinition => {
        applyFieldDefinition(validationContext, fieldDefinition, 'collectionRules');
      });

      return validationContext;
    });
  };

  const summariseResults = results => {
    let failedFieldCount = 0;
    for (const { assessedFields, issues } of results) {
      for (const field of assessedFields) {
        const { error = [], block = [] } = get(issues, [field], {});

        if (error.length || block.length) {
          failedFieldCount += 1;
        }
      }
    }

    const applicableFieldCount = sumBy(results, ({ assessedFields }) => assessedFields.size);

    const invalidProductCount = results.filter(({ issues }) => hasErrorsOrBlocks(issues)).length;
    const percentageComplete = applicableFieldCount
      ? Math.max(0, Math.floor(100 - (failedFieldCount / applicableFieldCount) * 100))
      : 100;

    return {
      applicableFieldCount,
      failedFieldCount,
      invalidProductCount,
      percentageComplete,
      productCount: results.length,
    };
  };

  const toPublicResult = ({ product, issues }) => ({
    product,
    issues: Object.keys(issues).length ? issues : null,
  });

  return {
    validateProduct: product => validateProductWithConfig(product, validationConfig),
    validateProducts: products => validateProductsWithConfig(products).map(toPublicResult),
    validateProductsWithSummary: products => {
      const validationResults = validateProductsWithConfig(products);
      return {
        results: validationResults.map(toPublicResult),
        summary: summariseResults(validationResults),
      };
    },
  };
};

const hasErrorsOrBlocks = issues =>
  Object.values(issues ?? {}).some(
    fieldIssues => fieldIssues.error?.length > 0 || fieldIssues.block?.length > 0
  );

module.exports = {
  createProductValidationOptions,
  createProductValidator,
};
